/**
 * Backend selection.
 *
 * Feature detection alone is not enough here: Firefox private windows expose
 * `indexedDB` and then reject the `open()` call, and Safari with "block all
 * cookies" exposes `localStorage` and throws on `setItem`. So each candidate is
 * probed by actually using it.
 */
import { storageKey } from '@/config/branding';
import type { ProjectStore } from './ProjectStore';
import { createIndexedDbStore } from './indexedDbStore';
import { createLocalStorageStore } from './localStorageStore';
import { createMemoryStore } from './memoryStore';

const PROBE_KEY = storageKey('probe');

async function probeIndexedDb(): Promise<ProjectStore | null> {
  if (typeof indexedDB === 'undefined' || indexedDB === null) return null;
  try {
    const store = createIndexedDbStore();
    // Forces the database open; this is where private-browsing modes fail.
    await store.list();
    return store;
  } catch {
    return null;
  }
}

function probeLocalStorage(): ProjectStore | null {
  try {
    const storage: Storage | undefined = globalThis.localStorage;
    if (!storage) return null;
    storage.setItem(PROBE_KEY, '1');
    storage.removeItem(PROBE_KEY);
    return createLocalStorageStore();
  } catch {
    return null;
  }
}

/**
 * Best backend available in this environment, in order of preference. Never
 * rejects: the in-memory store is always a valid last resort, so the app can
 * boot even with all persistence blocked.
 */
export async function createBestAvailableStore(): Promise<ProjectStore> {
  try {
    const indexed = await probeIndexedDb();
    if (indexed) return indexed;

    const local = probeLocalStorage();
    if (local) return local;
  } catch {
    // Fall through — an unusable environment is not a reason to fail booting.
  }
  return createMemoryStore();
}
